/**
 * IcaFlyerPage — standalone public marketing flyer served at /icaflyer.
 *
 * Replaces an outdated flyer that ICA embeds on their own website. This page
 * is intentionally NOT linked from anywhere in the app — it is reachable only
 * by typing the URL directly. The /icaflyer route is registered in App.tsx
 * outside every auth guard and outside the app layout shell (no header,
 * sidebar, or breadcrumbs), so the flyer renders standalone and screenshots
 * cleanly for ICA to embed as a static image.
 *
 * Design notes:
 *  - Portrait, fixed-width (768px) flyer so a screenshot is deterministic
 *    and reads well when ICA embeds it at a reduced size on their site.
 *  - Typography is intentionally large and section spacing is tight — this
 *    is a flyer, not a web page, and must stay legible when scaled down.
 *  - Colors are hardcoded from the app's active "rodeo" theme preset
 *    (config/ui/theme/theme-presets.ts): dark navy chrome, red primary,
 *    light cool-gray surfaces. Hardcoding (rather than reading CSS vars)
 *    keeps the flyer identical regardless of the visitor's light/dark mode.
 *  - The header uses the real HighCall bull-horn brand mark
 *    (public/icons/HighCallRodeo-icon-white.png).
 *
 * Copy lives in src/content/icaFlyerContent.ts.
 */

import { useEffect, type ReactNode } from 'react';
import { icaFlyerContent } from '../content/icaFlyerContent';

// "rodeo" theme preset (light), resolved to fixed hex so the flyer renders
// identically no matter the visitor's theme preference.
const C = {
  navy: '#252B37', // header / sidebar background
  navyDark: '#1B202A', // deeper navy for gradient depth
  navyLine: '#3A4150', // navy hairline / divider
  red: '#BD2828', // primary
  redDark: '#9C2020', // deeper red for gradient depth
  page: '#E7EBEF', // page background behind the flyer
  paper: '#FFFFFF', // flyer card surface
  tint: '#F6F8FA', // subtle cool-gray section tint
  redTint: '#FBECEC', // subtle red tint (matches app stat cards)
  ink: '#21242C', // primary text
  muted: '#5F6573', // secondary text
  border: '#DBDFE5', // hairline borders
} as const;

// The flyer may be embedded on ICA's own site. Every "highcallrodeo.com"
// mention is rendered as a real link so a click reaches the app — and
// target="_blank" ensures the click escapes an iframe if ICA embeds one.
const SITE_URL = 'https://highcallrodeo.com';
const URL_LABEL = 'highcallrodeo.com';

/**
 * Renders a string, turning every "highcallrodeo.com" occurrence into a
 * clickable link in the given color. Returns plain text when there is no
 * occurrence, so it is safe to apply to any copy string.
 */
function withLinks(text: string, linkColor: string): ReactNode[] {
  const segments = text.split(URL_LABEL);
  const nodes: ReactNode[] = [];
  segments.forEach((segment, index) => {
    if (segment) nodes.push(segment);
    if (index < segments.length - 1) {
      nodes.push(
        <a
          key={`hc-link-${index}`}
          href={SITE_URL}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: linkColor,
            fontWeight: 'inherit',
            textDecoration: 'underline',
            textUnderlineOffset: 2,
          }}
        >
          {URL_LABEL}
        </a>,
      );
    }
  });
  return nodes;
}

export default function IcaFlyerPage() {
  // Standalone page: set its own document title (App.tsx pages share "App").
  useEffect(() => {
    const previous = document.title;
    document.title = 'HighCall Rodeo — How It Works | ICA';
    return () => {
      document.title = previous;
    };
  }, []);

  const { brand, hero, steps, inApp, deadlines, contacts, footer } = icaFlyerContent;

  return (
    <div
      style={{
        minHeight: '100vh',
        background: C.page,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '40px 16px',
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
        color: C.ink,
      }}
    >
      {/* Fixed-width portrait flyer — sized for clean screenshotting / embedding. */}
      <article
        style={{
          width: 768,
          background: C.paper,
          borderRadius: 18,
          overflow: 'hidden',
          boxShadow: '0 24px 60px -16px rgba(27, 32, 42, 0.4)',
          border: `1px solid ${C.border}`,
        }}
      >
        {/* ---- Header band ---- */}
        <header
          style={{
            background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyDark} 100%)`,
            padding: '26px 44px',
            color: C.paper,
            display: 'flex',
            alignItems: 'center',
            gap: 22,
          }}
        >
          <img
            src="/icons/HighCallRodeo-icon-white.png"
            alt="HighCall Rodeo logo"
            width={78}
            height={78}
            style={{ flexShrink: 0, objectFit: 'contain' }}
          />
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontSize: 40,
                fontWeight: 800,
                letterSpacing: '-0.02em',
                lineHeight: 1.05,
              }}
            >
              {brand.wordmark}
            </div>
            <a
              href={SITE_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-block',
                fontSize: 20,
                color: '#FFFFFF',
                marginTop: 4,
                fontWeight: 700,
                textDecoration: 'underline',
                textUnderlineOffset: 2,
              }}
            >
              {brand.url}
            </a>
          </div>
          <span
            style={{
              background: C.red,
              color: '#FFFFFF',
              fontSize: 15,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              padding: '10px 18px',
              borderRadius: 9999,
              whiteSpace: 'nowrap',
            }}
          >
            {brand.badge}
          </span>
        </header>

        {/* Full-width red accent bar separating the header from the hero. */}
        <div style={{ height: 8, background: C.red }} />

        {/* ---- Hero ---- */}
        <section
          style={{
            padding: '30px 44px 26px',
            borderBottom: `1px solid ${C.border}`,
          }}
        >
          <h1
            style={{
              margin: 0,
              fontSize: 56,
              fontWeight: 800,
              letterSpacing: '-0.025em',
              lineHeight: 1.04,
              color: C.ink,
            }}
          >
            {hero.headline}
          </h1>
          <p
            style={{
              margin: '12px 0 0',
              fontSize: 25,
              fontWeight: 700,
              lineHeight: 1.45,
              color: C.muted,
            }}
          >
            {withLinks(hero.subhead, C.red)}
          </p>
        </section>

        {/* ---- Body ---- */}
        <div style={{ padding: '26px 44px 8px' }}>
          {/* Numbered setup steps */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
            {steps.map((step) => (
              <div
                key={step.number}
                style={{
                  background: C.tint,
                  border: `1px solid ${C.border}`,
                  borderRadius: 14,
                  padding: '22px 22px 24px',
                }}
              >
                <div
                  style={{
                    width: 54,
                    height: 54,
                    borderRadius: 9999,
                    background: C.red,
                    color: '#FFFFFF',
                    fontSize: 29,
                    fontWeight: 800,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: 14,
                  }}
                >
                  {step.number}
                </div>
                <div
                  style={{
                    fontSize: 29,
                    fontWeight: 800,
                    color: C.ink,
                    marginBottom: 8,
                    letterSpacing: '-0.01em',
                  }}
                >
                  {step.title}
                </div>
                <p
                  style={{
                    margin: 0,
                    fontSize: 21,
                    fontWeight: 700,
                    lineHeight: 1.45,
                    color: C.muted,
                  }}
                >
                  {withLinks(step.body, C.red)}
                </p>
              </div>
            ))}
          </div>

          {/* In-app workflow: Draw / Re-Entries / Trades */}
          <div style={{ marginTop: 24 }}>
            <h2
              style={{
                margin: '0 0 12px',
                fontSize: 18,
                fontWeight: 800,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                color: C.red,
              }}
            >
              {inApp.eyebrow}
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {inApp.items.map((item) => (
                <div
                  key={item.title}
                  style={{
                    display: 'flex',
                    gap: 20,
                    alignItems: 'flex-start',
                    background: C.paper,
                    border: `1px solid ${C.border}`,
                    borderLeft: `6px solid ${C.red}`,
                    borderRadius: 12,
                    padding: '18px 22px',
                  }}
                >
                  <div
                    style={{
                      width: 150,
                      flexShrink: 0,
                      fontSize: 26,
                      fontWeight: 800,
                      color: C.ink,
                      letterSpacing: '-0.01em',
                    }}
                  >
                    {item.title}
                  </div>
                  <p
                    style={{
                      margin: 0,
                      fontSize: 21,
                      fontWeight: 700,
                      lineHeight: 1.45,
                      color: C.muted,
                    }}
                  >
                    {item.body}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Key deadlines */}
          <div
            style={{
              marginTop: 24,
              background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyDark} 100%)`,
              borderRadius: 14,
              padding: '24px 30px',
              color: C.paper,
            }}
          >
            <div
              style={{
                fontSize: 18,
                fontWeight: 800,
                textTransform: 'uppercase',
                letterSpacing: '0.12em',
                color: '#AEB6C4',
                marginBottom: 14,
              }}
            >
              Key Deadlines
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
              {deadlines.map((deadline, index) => (
                <div
                  key={deadline.label}
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 18,
                    paddingTop: index === 0 ? 0 : 13,
                    borderTop: index === 0 ? 'none' : `1px solid ${C.navyLine}`,
                  }}
                >
                  <div
                    style={{
                      width: 184,
                      flexShrink: 0,
                      fontSize: 24,
                      fontWeight: 800,
                      color: '#FFFFFF',
                    }}
                  >
                    {deadline.label}
                  </div>
                  <div
                    style={{
                      fontSize: 23,
                      fontWeight: 700,
                      lineHeight: 1.4,
                      color: '#D5DAE3',
                    }}
                  >
                    {deadline.value}
                    {'emphasis' in deadline && deadline.emphasis ? (
                      <span
                        style={{
                          marginLeft: 12,
                          display: 'inline-block',
                          background: C.red,
                          color: '#FFFFFF',
                          fontSize: 14,
                          fontWeight: 800,
                          textTransform: 'uppercase',
                          letterSpacing: '0.06em',
                          padding: '4px 11px',
                          borderRadius: 9999,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {deadline.emphasis}
                      </span>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Contacts */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 18,
              marginTop: 20,
            }}
          >
            {contacts.map((contact) => (
              <div
                key={contact.label}
                style={{
                  background: C.redTint,
                  border: `1px solid ${C.border}`,
                  borderRadius: 12,
                  padding: '18px 22px',
                }}
              >
                <div
                  style={{
                    fontSize: 15,
                    fontWeight: 800,
                    textTransform: 'uppercase',
                    letterSpacing: '0.07em',
                    color: C.red,
                  }}
                >
                  {contact.label}
                </div>
                <div
                  style={{
                    fontSize: 31,
                    fontWeight: 800,
                    color: C.ink,
                    marginTop: 5,
                    letterSpacing: '-0.01em',
                  }}
                >
                  {contact.value}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ---- Footer band ---- */}
        <footer
          style={{
            marginTop: 24,
            background: `linear-gradient(135deg, ${C.red} 0%, ${C.redDark} 100%)`,
            color: '#FFFFFF',
            padding: '22px 44px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
          }}
        >
          <span style={{ fontSize: 25, fontWeight: 800, letterSpacing: '-0.01em' }}>
            {withLinks(footer, '#FFFFFF')}
          </span>
          <span
            style={{
              fontSize: 16,
              fontWeight: 700,
              color: '#F4D4D4',
              whiteSpace: 'nowrap',
            }}
          >
            In partnership with ICA
          </span>
        </footer>
      </article>
    </div>
  );
}
